import { useEffect, useState, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRelatedRecords } from "../../api/useRelatedRecords";
import { RecordContext } from "../../components/record/Record";
import { DataGrid, GridRowModel } from "@mui/x-data-grid";
import { useColumns } from "./columns";
import { useCalculateOrder } from "./utils";
import { OrdersFooter } from "./OrdersFooter";
import { convertApiItemsToGridRows } from "./utils";
import { Typography, Box } from "@mui/material";
import { useOrderInvoice } from "../../api/moloni/useOrderInvoice";
import { InvoiceDocumentsTable } from "./moloni/related-invoice-table/InvoiceDocumentsTable";
import { InvoiceSchedulesTable } from "./moloni/scheduled-invoices/InvoiceSchedulesTable";
import { calculateInvoicedNet } from "./moloni/invoiceDocumentsUtils";
import { OrderPayments } from "./payments/OrderPayments";

type OrdersDetailProps = {
    id?: number | string;
};

export const OrdersDetail = ({ id }: OrdersDetailProps) => {
    const recordContext = useContext(RecordContext);
    const orderId = id || recordContext?.item.id;
    const queryClient = useQueryClient();

    useEffect(() => {
        queryClient.invalidateQueries({ queryKey: ["order-invoice", orderId] });
    }, [orderId, queryClient]);

    const { data } = useRelatedRecords(
        "order_items",
        "*.*,service_variant.service.*.*,service_variant.agreement.*",
        "order_id",
        orderId
    );
    const { data: invoiceData, isLoading: isLoadingInvoice } = useOrderInvoice(orderId);
    const [rows, setRows] = useState<GridRowModel[] | []>([]);
    const columns = useColumns({
        rows: rows,
        type: "detail",
    });

    const calculateOrderTotal = useCalculateOrder();

    useEffect(() => {
        const records = data?.items;

        if (records && records.length > 0) {
            const items = convertApiItemsToGridRows(records);
            setRows(items);
        }
    }, [data]);

    const orderTotal = calculateOrderTotal(rows);
    const invoicedTotal = isLoadingInvoice ? null : calculateInvoicedNet(invoiceData);

    const orderItems =
        data?.items?.map((item: any) => ({
            serviceId: item.service_variant?.service?.id,
            serviceName: item.service_variant?.service?.name,
            quantity: item.quantity,
            price: item.price,
            discount: item.discount,
        })) || [];

    if (!data) return null;
    if (!recordContext) return null;

    return (
        <Box mt={1} ml={0.5} p={2} flexDirection="column" height={800} width="100%" sx={{ backgroundColor: "#fff" }}>
            <Box pt={6} pb={6}>
                <Typography variant="h5">Serviços Adquiridos:</Typography>
            </Box>
            <Box flexDirection="column" display="grid">
                {rows.length > 0 && (
                    <DataGrid
                        autoHeight
                        sx={{ width: "100%" }}
                        rows={rows}
                        columns={columns}
                        slots={{
                            footer: OrdersFooter,
                        }}
                        slotProps={{
                            footer: { orderTotal, recordContext, type: "detail" },
                        }}
                    />
                )}
            </Box>
            <OrderPayments
                orderId={orderId}
                orderTotal={orderTotal}
                paymentStatus={recordContext.item.payment_status}
                isReadOnly={true}
                recordType="detail"
            />
            <Box sx={{ mt: 5 }}>
                <InvoiceDocumentsTable
                    invoiceData={invoiceData}
                    isLoading={isLoadingInvoice}
                    orderDate={recordContext.item.order_date}
                />
            </Box>
            <InvoiceSchedulesTable
                orderId={orderId}
                treatmentId={recordContext.item.treatment}
                orderTotal={orderTotal}
                orderItems={orderItems}
                invoicedTotal={invoicedTotal}
            />
        </Box>
    );
};
